
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { WorkflowStep } from "@/types/workflow";
import { DocumentPreview } from "./DocumentPreview";
import { useDocumentGenerator } from "./DocumentGeneratorProvider";
import { Template } from "@/types/document-generator";
import { FileText, Plus, Upload, Shield } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CustomDocumentTab } from "./sections/CustomDocumentTab";
import { TemplateSelectionTab } from "./sections/TemplateSelectionTab";
import { useDocumentAnalysis } from "@/hooks/useDocumentAnalysis";
import { DocumentAnalysisResult } from "./DocumentAnalysisResult";
import { Button } from "@/components/ui/button";
import { FileUploadSection } from "./sections/FileUploadSection";
import { useToast } from "@/hooks/use-toast";

interface DocumentGeneratorContentProps {
  step?: WorkflowStep | null;
  existingDocuments?: any[];
  onClose?: () => void;
  onSuccess?: () => void;
  isWorkflowGeneration?: boolean;
  setPreviewContent?: (content: string) => void;
}

export function DocumentGeneratorContent({ 
  step,
  existingDocuments,
  onClose,
  onSuccess,
  isWorkflowGeneration = false,
  setPreviewContent
}: DocumentGeneratorContentProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const { isLoading, previewContent, form, generateDocument } = useDocumentGenerator();
  const [customTitle, setCustomTitle] = useState("");
  const [customContent, setCustomContent] = useState("");
  const [analysisResult, setAnalysisResult] = useState<any>(null);
  const { analyzeDocument, isAnalyzing } = useDocumentAnalysis();
  const { toast } = useToast();

  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['document-templates'],
    queryFn: async () => {
      console.log('Fetching document templates...');
      const { data, error } = await supabase
        .from('document_templates')
        .select('*')
        .order('category');
      
      if (error) {
        console.error('Error fetching templates:', error);
        throw error;
      }
      
      console.log('Fetched templates:', data);
      return data || [];
    },
    enabled: !isWorkflowGeneration
  });

  const handleCustomDocumentSubmit = async () => {
    if (customTitle && customContent) {
      form.setValue("title", customTitle);
      form.setValue("content", customContent);
      await generateDocument();
      if (onSuccess) onSuccess();
    }
  };

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
    form.setValue("title", template.title);
    form.setValue("content", template.content);
    generateDocument();
    if (onSuccess) onSuccess();
  };

  const handleAnalyzePreview = async () => {
    if (!previewContent) {
      toast({
        variant: "destructive",
        title: "Ingen innhold",
        description: "Generer et dokument først før du analyserer.",
      });
      return;
    }

    try {
      const result = await analyzeDocument("preview", previewContent);
      setAnalysisResult(result);
    } catch (error) {
      console.error('Error analyzing document:', error);
    }
  };

  const handleFileAnalysis = async (content: string) => {
    try {
      const result = await analyzeDocument("uploaded", content);
      setAnalysisResult(result);
    } catch (error) {
      console.error('Error analyzing uploaded document:', error);
    }
  };

  if (isWorkflowGeneration) {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <h2 className="text-2xl font-semibold p-6 border-b">Generer dokument for {step?.title}</h2>
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-32" />
            </div>
          ) : (
            <>
              {analysisResult && (
                <div className="mb-6">
                  <DocumentAnalysisResult result={analysisResult} />
                </div>
              )}
              <DocumentPreview
                content={previewContent}
                isEditing={false}
                isLoading={isLoading}
                onEdit={() => {}}
                onSave={onSuccess || (() => {})}
                onContentChange={setPreviewContent || (() => {})}
              />
              <div className="mt-4 flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleAnalyzePreview}
                  disabled={isAnalyzing || !previewContent}
                  className="flex items-center gap-2"
                >
                  <Shield className="h-4 w-4" />
                  {isAnalyzing ? 'Analyserer...' : 'Analyser dokument'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-h-[90vh] overflow-hidden">
      <div className="p-6 border-b">
        <h2 className="text-2xl font-semibold">Opprett nytt dokument</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Opprett et nytt dokument fra bunnen av, velg en mal, eller last opp et dokument for analyse
        </p>
      </div>
      
      <Tabs defaultValue="custom" className="flex-1 overflow-hidden">
        <TabsList className="mx-6 mt-2">
          <TabsTrigger value="custom" className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Nytt dokument
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Bruk mal
          </TabsTrigger>
          <TabsTrigger value="analyze" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Analyser dokument
          </TabsTrigger>
        </TabsList>

        <TabsContent value="custom" className="flex-1 overflow-hidden mt-0">
          <CustomDocumentTab
            title={customTitle}
            content={customContent}
            onTitleChange={setCustomTitle}
            onContentChange={setCustomContent}
            onSubmit={handleCustomDocumentSubmit}
            isLoading={isLoading}
          />
          {previewContent && (
            <div className="p-4">
              {analysisResult && (
                <div className="mb-6">
                  <DocumentAnalysisResult result={analysisResult} />
                </div>
              )}
              <Button
                variant="outline"
                onClick={handleAnalyzePreview}
                disabled={isAnalyzing || !previewContent}
                className="flex items-center gap-2"
              >
                <Shield className="h-4 w-4" />
                {isAnalyzing ? 'Analyserer...' : 'Analyser dokument'}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates" className="flex-1 overflow-hidden mt-0">
          <TemplateSelectionTab
            templates={templates || []}
            templatesLoading={templatesLoading}
            onSelectTemplate={handleTemplateSelect}
          />
          {previewContent && (
            <div className="p-4">
              {analysisResult && (
                <div className="mb-6">
                  <DocumentAnalysisResult result={analysisResult} />
                </div>
              )}
              <Button
                variant="outline"
                onClick={handleAnalyzePreview}
                disabled={isAnalyzing || !previewContent}
                className="flex items-center gap-2"
              >
                <Shield className="h-4 w-4" />
                {isAnalyzing ? 'Analyserer...' : 'Analyser dokument'}
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="analyze" className="flex-1 overflow-y-auto mt-0 p-6">
          <div className="space-y-6">
            <FileUploadSection form={form} onContentRead={handleFileAnalysis} />
            {analysisResult && (
              <DocumentAnalysisResult result={analysisResult} />
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
